import { IGenericJSON } from '../helpers/interface';

/**
 * Mirrors MEETING_STATUSES in server/constants/meetingStatus.js. Each value
 * records a decision; clock-dependent states are derived below, not stored.
 */
export type MeetingStatus = 'pending' | 'confirmed' | 'declined' | 'canceled';

/**
 * What a meeting is called in the UI — status with the clock folded in. Shared by
 * both views, and a partition: exactly one state per meeting, so a row can't
 * appear under two filters.
 *
 *   confirmed (future) →  upcoming     pending (future) →  pending
 *   confirmed (past)   →  previous     pending (past)   →  expired
 *   declined           →  denied       canceled         →  canceled
 */
export type MeetingState = 'upcoming' | 'pending' | 'previous' | 'canceled' | 'denied' | 'expired';

/** The one place each state's wording is decided. */
export const MEETING_STATE_LABEL: Record<MeetingState, string> = {
  upcoming: 'Upcoming',
  pending: 'Pending',
  previous: 'Previous',
  canceled: 'Canceled',
  denied: 'Denied',
  expired: 'Expired',
};

export class Meeting {
  id: number;
  title: string | '';
  description: string | null;

  meetingDate: Date;
  startTime: string | null;
  duration: number | null;

  status: MeetingStatus;
  location: string | null;
  meetingLink: string | null;
  requestedChanges: {
    meetingDate: string;
    startTime: string;
    duration: number;
    location?: string | null;
    /** Why the proposal was made, never applied to
     *  `note` field when the proposal is accepted. */
    message?: string | null;
    proposedByUserId: number;
  } | null;
  /** True when a proposal on this still pending meeting was accepted by the
   *  other participant but hasn't been confirmed by the faculty member yet.
   *  drives the "Changes accepted" label once requestedChanges clears. */
  changesAccepted: boolean;
  note: string | null;

  facultyName: string;
  studentName: string;
  gradeLevel: number | null;
  studentId: number;
  facultyUserId: number;
  studentUserId: number;

  constructor(data: IGenericJSON) {
    this.id = data['id'];
    this.title = data['title'] ?? '';
    this.description = data['description'] ?? null;

    this.meetingDate = Meeting.parseLocalDate(data['meetingDate']);
    this.startTime = data['startTime'] ?? null;
    this.duration = data['duration'] ?? null;

    this.status = data['status'];
    this.location = data['location'] ?? null;
    this.meetingLink = data['meetingLink'] ?? null;
    this.requestedChanges = data['requestedChanges'] ?? null;
    this.changesAccepted = data['changesAccepted'] ?? false;
    this.note = data['note'] ?? null;

    const facultyUser = data['facultyUser'];
    this.facultyName = facultyUser
      ? `${facultyUser.first_name ?? ''} ${facultyUser.last_name ?? ''}`.trim()
      : (data['facultyName'] ?? '');

    const s = data['student'];
    this.studentName = s?.user
      ? `${s.user.first_name ?? ''} ${s.user.last_name ?? ''}`.trim()
      : (data['studentName'] ?? '');
    this.gradeLevel = s?.grade_level ?? data['gradeLevel'] ?? null;

    this.studentId = data['student_id'] ?? data['studentId'];
    this.facultyUserId = data['faculty_user_id'] ?? data['facultyUserId'];
    this.studentUserId = data['student_user_id'] ?? data['studentUserId'];
  }

  /**
   * Parses just the calendar day from a date string/Date into a LOCAL midnight
   * Date, so a UTC-serialized DATE column doesn't shift the day in timezones
   * behind UTC.
   */
  private static parseLocalDate(raw: string | Date): Date {
    const s = typeof raw === 'string' ? raw : new Date(raw).toISOString();
    const [y, m, d] = s.slice(0, 10).split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  }

  // Combine a date and time string into a single Date object, optionally adding minutes to the result. Returns null if the time is null.
  private static combine(date: Date, time: string | null, addMinutes = 0): Date | null {
    if (!time) return null;
    const [hours, minutes, seconds] = time.split(':').map(Number);
    const mdate = new Date(date);
    mdate.setHours(hours, minutes, seconds || 0, 0);
    if (addMinutes) mdate.setMinutes(mdate.getMinutes() + addMinutes);
    return mdate;
  }

  get start(): Date | null {
    return Meeting.combine(this.meetingDate, this.startTime);
  }

  get end(): Date | null {
    return Meeting.combine(this.meetingDate, this.startTime, this.duration ?? 0);
  }

  /** True whenever a proposal from either participant is awaiting a response. */
  get isChangeRequest(): boolean {
    return this.requestedChanges !== null;
  }

  /** True once the meeting's end time has passed */
  get isPast(): boolean {
    return !!this.end && this.end < new Date();
  }

  get proposalIsLapsed(): boolean {
    const proposedEnd = this.proposedEnd;
    return !!proposedEnd && proposedEnd < new Date();
  }

  get hasLiveProposal(): boolean {
    return this.requestedChanges !== null && !this.proposalIsLapsed;
  }

  get isLapsed(): boolean {
    return this.status === 'pending' && this.isPast && !this.hasLiveProposal;
  }

  /**
   * True while the meeting is still on the books, which gates the reschedule and
   * cancel actions. A past confirmed meeting stays active — it happened and can
   * still be acted on; a past pending one has lapsed and cannot.
   *
   * Defined by inclusion so a status added later doesn't inherit those actions.
   */
  get isActive(): boolean {
    return this.status === 'confirmed' || (this.status === 'pending' && !this.isPast);
  }

  /**
   * The bucket used by both filter dropdowns and the row badge. Exhaustive over
   * MeetingStatus, so a new status fails to compile rather than defaulting.
   */
  get state(): MeetingState {
    switch (this.status) {
      case 'canceled':
        return 'canceled';
      case 'declined':
        return 'denied';
      case 'pending':
        return this.isLapsed ? 'expired' : 'pending';
      case 'confirmed':
        return this.isPast ? 'previous' : 'upcoming';
      default: {
        const unhandled: never = this.status;
        return unhandled;
      }
    }
  }

  /** How the state reads on screen. */
  get stateLabel(): string {
    return MEETING_STATE_LABEL[this.state];
  }

  /** True once the meeting won't happen live — its time passed, or it was
   *  called off or lapsed before then. A meeting canceled for next week
   *  shouldn't keep a live "join" link until that time arrives. */
  get isOver(): boolean {
    return this.isPast || !this.isActive;
  }

  get proposedStart(): Date | null {
    const rc = this.requestedChanges;
    return rc ? Meeting.combine(Meeting.parseLocalDate(rc.meetingDate), rc.startTime) : null;
  }

  get proposedEnd(): Date | null {
    const rc = this.requestedChanges;
    return rc
      ? Meeting.combine(Meeting.parseLocalDate(rc.meetingDate), rc.startTime, rc.duration ?? 0)
      : null;
  }

  get proposedLocation(): string | null {
    return this.requestedChanges?.location ?? null;
  }

  get proposedMessage(): string | null {
    return this.requestedChanges?.message ?? null;
  }

  /**
   * The id of the participant whose turn it is to act.
   * (proposed changes or new pending request).
   * Null once nothing is waiting on anyone — including a request that lapsed
   * unanswered, which no longer needs a response and so is kept out of the
   * pending badge's count as well as the pending list.
   */
  get awaitingResponseUserId(): number | null {
    const proposal = this.requestedChanges;
    if (proposal && this.hasLiveProposal) {
      return proposal.proposedByUserId === this.facultyUserId
        ? this.studentUserId
        : this.facultyUserId;
    }
    if (this.status === 'pending') return this.isLapsed ? null : this.facultyUserId;
    return null;
  }

  get meetingType() {
    return this.meetingLink ? 'remote' : 'in-person';
  }

  get meetingLocation(): string | null {
    // A real remote link, once the counselor has generated it.
    if (this.meetingLink && this.meetingLink !== 'Link pending') return this.meetingLink;
    // Remote-but-pending has no usable link yet (card shows "Link pending");
    // in-person shows its room/location.
    return this.meetingType === 'in-person' ? this.location : null;
  }

  public static digest(data: IGenericJSON): Meeting {
    return new Meeting(data);
  }

  public static digestResponse(data: IGenericJSON[]): Meeting[] {
    const ret: Meeting[] = [];
    data.forEach((u: IGenericJSON): void => {
      ret.push(this.digest(u));
    });
    return ret;
  }
}
